// components/ViewRoutes/ViewRoutesList.jsx
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  getViewRoutes, 
  deactivateViewRoute,
  getViewRouteStats 
} from '../../Services/Operations/ProductAPI';
import '../../Styles/ManagementPages.css';
import '../../Styles/CustomerManage.css';

const formatDateDMY = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

// Skeleton Components for Loading State
const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Route Number</th>
          <th>Route Name</th>
          <th>Local Name</th>
          <th>Origin</th>
          <th>Destination</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const ViewRoutesList = () => {
  const { token } = useSelector((state) => state.user);
  const [viewRoutes, setViewRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [viewingRoute, setViewingRoute] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRoutes, setTotalRoutes] = useState(0);
  const [pageSize] = useState(10);

  const [filters, setFilters] = useState({
    isActive: 'true',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchViewRoutes();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, filters.isActive, filters.sortBy, filters.sortOrder]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchViewRoutes = async () => {
    try {
      setSearching(true);
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        isActive: filters.isActive,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      const response = await getViewRoutes(token, params);

      if (response && response.success) {
        const transformedRoutes = response.data.map((route, index) => ({
          id: route._id,
          serialNo: (currentPage - 1) * pageSize + index + 1,
          routeNumber: route.routeNumber || 'N/A',
          routeName: route.routeName || 'N/A',
          localRouteName: route.localRouteName || '-',
          origin: route.origin?.city || 'N/A',
          destination: route.destination?.city || 'N/A',
          isRoundTrip: route.isRoundTrip || false,
          isActive: route.isActive,
          createdAt: route.createdAt,
          updatedAt: route.updatedAt,
          createdBy: route.createdBy,
          createdByName: route.createdByName,
          createdByEmail: route.createdByEmail,
          createdByPhone: route.createdByPhone,
          routeDetails: route.routeDetails,
          intermediateStops: route.intermediateStops || [],
          returnJourney: route.returnJourney,
          timeSlots: route.timeSlots || [],
          originDetails: route.origin,
          destinationDetails: route.destination,
          isMultiTrip: route.isMultiTrip,
          numberOfTrips: route.numberOfTrips,
          via: route.via || [],
          fareMatrix: route.fareMatrix || [],
          additionalBoardingPoints: route.additionalBoardingPoints || [],
          additionalDroppingPoints: route.additionalDroppingPoints || [],
          additionalNullPoints: route.additionalNullPoints || [],
          originalRouteId: route.originalRouteId,
          isViewOnly: route.isViewOnly,
          __v: route.__v
        }));

        setViewRoutes(transformedRoutes);
        setTotalPages(response.pagination?.pages || 1);
        setTotalRoutes(response.pagination?.total || 0);
      } else {
        throw new Error(response?.message || 'Failed to fetch view routes');
      }
    } catch (err) {
      setError('Failed to fetch view routes. Please try again.');
      console.error('Error fetching view routes:', err);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getViewRouteStats(token);
      if (response && response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleViewDetails = (route) => {
    setViewingRoute(route);
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this view route?')) return;
    
    try {
      const response = await deactivateViewRoute(id, token);
      if (response && response.success) {
        alert('View route deactivated successfully!');
        fetchViewRoutes();
        fetchStats();
      } else {
        throw new Error(response?.message || 'Failed to deactivate');
      }
    } catch (error) {
      console.error('Error deactivating view route:', error);
      alert('Failed to deactivate view route');
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setFilters({
      isActive: 'true',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    fetchViewRoutes();
    fetchStats();
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="status-badge active">Active</span>
    ) : (
      <span className="status-badge inactive">Inactive</span>
    );
  };

  const getTripBadge = (isRoundTrip) => {
    return isRoundTrip ? (
      <span className="status-badge round-trip">🔄 Round Trip</span>
    ) : (
      <span className="status-badge one-way">➡️ One Way</span>
    );
  };

  const closeViewModal = () => {
    setViewingRoute(null);
  };

  const renderLocationDetails = (location, label) => {
    if (!location) return null;
    return (
      <div className="detail-row">
        <label>{label}:</label>
        <span>
          <div><strong>{location.city}</strong>, {location.state}</div>
          <div style={{ fontSize: '13px', color: '#555' }}>{location.address}</div>
          {location.terminal && <div style={{ fontSize: '13px', color: '#666' }}>Terminal: {location.terminal}</div>}
          {location.departureTime && <div style={{ fontSize: '13px', color: '#666' }}>Departure: {location.departureTime}</div>}
          {location.arrivalTime && <div style={{ fontSize: '13px', color: '#666' }}>Arrival: {location.arrivalTime}</div>}
          {location.distanceFromOrigin !== undefined && (
            <div style={{ fontSize: '13px', color: '#666' }}>Distance: {location.distanceFromOrigin} km</div>
          )}
          {location.coordinates && (
            <div style={{ fontSize: '12px', color: '#888' }}>
              Lat: {location.coordinates.lat}, Lng: {location.coordinates.lng}
            </div>
          )}
          {location.timings && location.timings.length > 0 && (
            <div style={{ fontSize: '12px', color: '#888' }}>
              Timings: {location.timings.map((t, i) => (
                <span key={i}>
                  {t.departureTime && `Depart: ${t.departureTime}`}
                  {t.arrivalTime && ` Arrival: ${t.arrivalTime}`}
                  {i < location.timings.length - 1 && ' | '}
                </span>
              ))}
            </div>
          )}
        </span>
      </div>
    );
  };

  const renderStopList = (stops, title) => {
    if (!stops || stops.length === 0) return null;
    return (
      <div className="bookings-section">
        <h4>{title}</h4>
        <div className="bookings-table-container">
          <table className="bookings-table">
            <thead>
              <tr>
                <th>#</th>
                <th>City</th>
                <th>State</th>
                <th>Address</th>
                <th>Stop Type</th>
                <th>Distance</th>
                <th>Arrival</th>
                <th>Departure</th>
                <th>Manual Stop</th>
              </tr>
            </thead>
            <tbody>
              {stops.map((stop, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td><strong>{stop.city}</strong></td>
                  <td>{stop.state}</td>
                  <td style={{ fontSize: '12px' }}>{stop.address}</td>
                  <td>
                    <span className={`status-badge ${stop.stopType || 'none'}`}>
                      {stop.stopType || 'none'}
                    </span>
                  </td>
                  <td>{stop.distanceFromOrigin || 0} km</td>
                  <td>{stop.arrivalTime || 'N/A'}</td>
                  <td>{stop.departureTime || 'N/A'}</td>
                  <td>{stop.isManualStop ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAdditionalPoints = (points, title) => {
    if (!points || points.length === 0) return null;
    return (
      <div className="bookings-section">
        <h4>{title}</h4>
        <div className="bookings-table-container">
          <table className="bookings-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>City</th>
                <th>State</th>
                <th>Address</th>
                <th>Landmark</th>
                <th>Pickup/Drop Time</th>
              </tr>
            </thead>
            <tbody>
              {points.map((point, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{point.name || '-'}</td>
                  <td>{point.city}</td>
                  <td>{point.state}</td>
                  <td style={{ fontSize: '12px' }}>{point.address}</td>
                  <td>{point.landmark || '-'}</td>
                  <td>{point.pickupTime || point.dropTime || point.referenceTime || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderFareMatrix = (fareMatrix, title) => {
    if (!fareMatrix || fareMatrix.length === 0) return null;
    return (
      <div className="bookings-section">
        <h4>{title}</h4>
        <div className="bookings-table-container">
          <table className="bookings-table">
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Distance (km)</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              {fareMatrix.map((fare, idx) => (
                <React.Fragment key={idx}>
                  {fare.toCells && fare.toCells.map((cell, cellIdx) => (
                    <tr key={`${idx}-${cellIdx}`}>
                      <td><strong>{fare.fromName || fare.fromId}</strong></td>
                      <td><strong>{cell.toName || cell.toId}</strong></td>
                      <td>{cell.distanceKm || 0}</td>
                      <td>₹{cell.price || 0}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Loading State
  if (loading && viewRoutes.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>
        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>
        <SkeletonTable />
        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>View Routes Management</h1>
        <div className="header-actions">
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by route name, number or local name..."
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: "70%" }}
            />
            {searching && (
              <span style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
              }}>
                searching...
              </span>
            )}
          </div>
          <select
            value={filters.isActive}
            onChange={(e) => handleFilterChange('isActive', e.target.value)}
            className="filter-select"
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="true">Active Only</option>
            <option value="false">Inactive Only</option>
            <option value="">All Routes</option>
          </select>
          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total View Routes</h3>
          <p>{stats?.total || 0}</p>
          <span>All view routes</span>
        </div>
        <div className="stat-card">
          <h3>Active Routes</h3>
          <p>{stats?.totalActive || 0}</p>
          <span>Currently active</span>
        </div>
       
        <div className="stat-card">
          <h3>Unique Creators</h3>
          <p>{stats?.createdByStats?.length || 0}</p>
          <span>Total creators</span>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Route Number</th>
              <th>Route Name</th>
              <th>Local Name</th>
              <th>Origin</th>
              <th>Destination</th>
              <th>Type</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {viewRoutes.map((route) => (
              <tr key={route.id}>
                <td>{route.serialNo}</td>
                <td className="route-number">{route.routeNumber}</td>
                <td className="route-name">{route.routeName}</td>
                <td>{route.localRouteName}</td>
                <td>{route.origin}</td>
                <td>{route.destination}</td>
                <td>{getTripBadge(route.isRoundTrip)}</td>
                <td>{getStatusBadge(route.isActive)}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn view"
                      onClick={() => handleViewDetails(route)}
                    >
                      View
                    </button>
                    
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {viewRoutes.length === 0 && !loading && (
          <div className="no-data">
            <p>No view routes found matching your search.</p>
          </div>
        )}
      </div>

      <div className="pagination-container">
        <div className="pagination-info">
          Showing {viewRoutes.length} of {totalRoutes} routes (Page {currentPage} of {totalPages})
          {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = i + 1;
              } else if (currentPage <= 3) {
                pageNumber = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + i;
              } else {
                pageNumber = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNumber}
                  className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                  onClick={() => handlePageClick(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>
          <button
            className="pagination-btn"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* View Details Modal - Full Response */}
      {viewingRoute && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>View Route Details - Full Response</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* Basic Route Information */}
              <div className="customer-details">
                <h4 style={{ marginBottom: '10px', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>
                  📋 Basic Information
                </h4>
                
                <div className="detail-row">
                  <label>ID:</label>
                  <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>{viewingRoute.id}</span>
                </div>
                <div className="detail-row">
                  <label>Route Number:</label>
                  <span>{viewingRoute.routeNumber}</span>
                </div>
                <div className="detail-row">
                  <label>Route Name:</label>
                  <span><strong>{viewingRoute.routeName}</strong></span>
                </div>
                <div className="detail-row">
                  <label>Local Name:</label>
                  <span>{viewingRoute.localRouteName}</span>
                </div>
                <div className="detail-row">
                  <label>Original Route ID:</label>
                  <span>{viewingRoute.originalRouteId || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Route Type:</label>
                  <span>{getTripBadge(viewingRoute.isRoundTrip)}</span>
                </div>
                <div className="detail-row">
                  <label>Multi Trip:</label>
                  <span>{viewingRoute.isMultiTrip ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <label>Number of Trips:</label>
                  <span>{viewingRoute.numberOfTrips || 1}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span>{getStatusBadge(viewingRoute.isActive)}</span>
                </div>
                <div className="detail-row">
                  <label>Is View Only:</label>
                  <span>{viewingRoute.isViewOnly ? 'Yes' : 'No'}</span>
                </div>
              </div>

              {/* Time Slots */}
              {viewingRoute.timeSlots && viewingRoute.timeSlots.length > 0 && (
                <div className="bookings-section">
                  <h4>⏰ Time Slots</h4>
                  <div className="bookings-table-container">
                    <table className="bookings-table">
                      <thead>
                        <tr>
                          <th>Sequence</th>
                          <th>Departure</th>
                          <th>Arrival</th>
                          <th>Fare Multiplier</th>
                          <th>Active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingRoute.timeSlots.map((slot, idx) => (
                          <tr key={idx}>
                            <td>{slot.sequence}</td>
                            <td>{slot.departureTime || 'N/A'}</td>
                            <td>{slot.arrivalTime || 'N/A'}</td>
                            <td>{slot.fareMultiplier || 1}</td>
                            <td>{slot.isActive ? '✅' : '❌'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Origin Details */}
              {viewingRoute.originDetails && (
                <div className="bookings-section">
                  <h4>🚌 Origin Details</h4>
                  {renderLocationDetails(viewingRoute.originDetails, 'Origin')}
                </div>
              )}

              {/* Destination Details */}
              {viewingRoute.destinationDetails && (
                <div className="bookings-section">
                  <h4>📍 Destination Details</h4>
                  {renderLocationDetails(viewingRoute.destinationDetails, 'Destination')}
                </div>
              )}

              {/* Route Details */}
              {viewingRoute.routeDetails && (
                <div className="bookings-section">
                  <h4>📏 Route Details</h4>
                  <div className="customer-details">
                    <div className="detail-row">
                      <label>Total Distance:</label>
                      <span>{viewingRoute.routeDetails.totalDistance} km</span>
                    </div>
                    <div className="detail-row">
                      <label>Estimated Duration:</label>
                      <span>
                        {viewingRoute.routeDetails.estimatedDuration?.hours || 0}h {' '}
                        {viewingRoute.routeDetails.estimatedDuration?.minutes || 0}m
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Intermediate Stops */}
              {renderStopList(viewingRoute.intermediateStops, '🛑 Intermediate Stops')}

              {/* Additional Boarding Points */}
              {renderAdditionalPoints(viewingRoute.additionalBoardingPoints, '⬆️ Additional Boarding Points')}

              {/* Additional Dropping Points */}
              {renderAdditionalPoints(viewingRoute.additionalDroppingPoints, '⬇️ Additional Dropping Points')}

              {/* Additional Null Points */}
              {renderAdditionalPoints(viewingRoute.additionalNullPoints, '⬛ Additional Null Points')}

              {/* Fare Matrix */}
              {renderFareMatrix(viewingRoute.fareMatrix, '💰 Fare Matrix')}

              {/* Via Points */}
              {viewingRoute.via && viewingRoute.via.length > 0 && (
                <div className="bookings-section">
                  <h4>🔄 Via Points</h4>
                  <div className="bookings-table-container">
                    <table className="bookings-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>City</th>
                          <th>State</th>
                          <th>Sequence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingRoute.via.map((via, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{via.city}</td>
                            <td>{via.state}</td>
                            <td>{via.sequence}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Return Journey - Full Details */}
              {viewingRoute.isRoundTrip && viewingRoute.returnJourney && (
                <div className="bookings-section" style={{ borderTop: '3px solid #007bff', marginTop: '20px', paddingTop: '20px' }}>
                  <h3 style={{ color: '#007bff' }}>🔄 Return Journey - Full Details</h3>
                  
                  {/* Return Journey Basic Info */}
                  <div className="customer-details">
                    <div className="detail-row">
                      <label>Return Active:</label>
                      <span>{viewingRoute.returnJourney.isActive ? 'Yes' : 'No'}</span>
                    </div>
                  
                  </div>

                  {/* Return Origin */}
                  {viewingRoute.returnJourney.origin && (
                    <div className="bookings-section">
                      <h4>🔙 Return Origin</h4>
                      {renderLocationDetails(viewingRoute.returnJourney.origin, 'Return Origin')}
                    </div>
                  )}

                  {/* Return Destination */}
                  {viewingRoute.returnJourney.destination && (
                    <div className="bookings-section">
                      <h4>🔜 Return Destination</h4>
                      {renderLocationDetails(viewingRoute.returnJourney.destination, 'Return Destination')}
                    </div>
                  )}

                  {/* Return Route Details */}
                  {viewingRoute.returnJourney.routeDetails && (
                    <div className="bookings-section">
                      <h4>📏 Return Route Details</h4>
                      <div className="customer-details">
                        <div className="detail-row">
                          <label>Return Total Distance:</label>
                          <span>{viewingRoute.returnJourney.routeDetails.totalDistance} km</span>
                        </div>
                        <div className="detail-row">
                          <label>Return Estimated Duration:</label>
                          <span>
                            {viewingRoute.returnJourney.routeDetails.estimatedDuration?.hours || 0}h {' '}
                            {viewingRoute.returnJourney.routeDetails.estimatedDuration?.minutes || 0}m
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Return Intermediate Stops */}
                  {renderStopList(viewingRoute.returnJourney.intermediateStops, '🛑 Return Intermediate Stops')}

                  {/* Return Additional Boarding Points */}
                  {renderAdditionalPoints(viewingRoute.returnJourney.additionalBoardingPoints, '⬆️ Return Additional Boarding Points')}

                  {/* Return Additional Dropping Points */}
                  {renderAdditionalPoints(viewingRoute.returnJourney.additionalDroppingPoints, '⬇️ Return Additional Dropping Points')}

                  {/* Return Additional Null Points */}
                  {renderAdditionalPoints(viewingRoute.returnJourney.additionalNullPoints, '⬛ Return Additional Null Points')}

                  {/* Return Fare Matrix */}
                  {renderFareMatrix(viewingRoute.returnJourney.fareMatrix, '💰 Return Fare Matrix')}

                  {/* Return Via Points */}
                  {viewingRoute.returnJourney.via && viewingRoute.returnJourney.via.length > 0 && (
                    <div className="bookings-section">
                      <h4>🔄 Return Via Points</h4>
                      <div className="bookings-table-container">
                        <table className="bookings-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>City</th>
                              <th>State</th>
                              <th>Sequence</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viewingRoute.returnJourney.via.map((via, idx) => (
                              <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td>{via.city}</td>
                                <td>{via.state}</td>
                                <td>{via.sequence}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Created By Information */}
              <div className="bookings-section" style={{ borderTop: '2px solid #e9ecef', marginTop: '20px', paddingTop: '20px' }}>
                <h4>👤 Created By Information</h4>
                <div className="customer-details">
                  <div className="detail-row">
                    <label>Created By Name:</label>
                    <span><strong>{viewingRoute.createdByName || 'Unknown'}</strong></span>
                  </div>
                  <div className="detail-row">
                    <label>Created By Email:</label>
                    <span>{viewingRoute.createdByEmail}</span>
                  </div>
                  <div className="detail-row">
                    <label>Created By Phone:</label>
                    <span>{viewingRoute.createdByPhone || 'N/A'}</span>
                  </div>
                  {viewingRoute.createdBy && (
                    <>
                      <div className="detail-row">
                        <label>Creator ID:</label>
                        <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>{viewingRoute.createdBy._id}</span>
                      </div>
                      <div className="detail-row">
                        <label>Creator Name:</label>
                        <span>{viewingRoute.createdBy.name}</span>
                      </div>
                      <div className="detail-row">
                        <label>Creator Email:</label>
                        <span>{viewingRoute.createdBy.email}</span>
                      </div>
                      <div className="detail-row">
                        <label>Creator Phone:</label>
                        <span>{viewingRoute.createdBy.phone || 'N/A'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Timestamps */}
              <div className="bookings-section">
                <h4>⏱️ Timestamps</h4>
                <div className="customer-details">
                  <div className="detail-row">
                    <label>Created At:</label>
                    <span>{formatDateTime(viewingRoute.createdAt)}</span>
                  </div>
                  <div className="detail-row">
                    <label>Updated At:</label>
                    <span>{formatDateTime(viewingRoute.updatedAt)}</span>
                  </div>
                  <div className="detail-row">
                    <label>Version:</label>
                    <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>v{viewingRoute.__v || 0}</span>
                  </div>
                </div>
              </div>

              {/* Full JSON Response - Collapsible */}
              <div className="bookings-section" style={{ borderTop: '2px solid #e9ecef', marginTop: '20px', paddingTop: '20px' }}>
                <h4>📄 Full JSON Response</h4>
                <details style={{ marginTop: '10px' }}>
                  <summary style={{ cursor: 'pointer', color: '#007bff', fontWeight: '500' }}>
                    Click to expand full response
                  </summary>
                  <pre style={{
                    background: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '6px',
                    overflow: 'auto',
                    maxHeight: '400px',
                    fontSize: '12px',
                    border: '1px solid #e9ecef',
                    marginTop: '10px'
                  }}>
                    {JSON.stringify(viewingRoute, null, 2)}
                  </pre>
                </details>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
              
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewRoutesList;